import { createSlice } from '@reduxjs/toolkit';

import type { RecordDetailsForm } from '@app/components/RecordModal/components/RecordDetails/types';
import { MenuTabEnum } from '@app/components/RecordModal/types';
import type { Warnings } from '@app/services/rtkQuery/Records/types';
import type {
  OtherRecordsInterface,
  RecordInterface,
  TEmployeeData,
  UpdatedEmployeeInterface,
} from '@app/types';

import type { PayloadAction } from '@reduxjs/toolkit';
export type ModalMode = 'create' | 'edit';

interface RecordModalPayload {
  mode: ModalMode;
  record?: RecordInterface | null;
  employee?: UpdatedEmployeeInterface | null;
  time?: string | null;
  // Конец предложенного отрезка: журнал передаёт его, когда шаг сетки
  // частично занят записью и свободен только до её начала (issue/11151).
  endTime?: string | null;
  clientId?: number;
  clientName?: string | null;
  branchId?: number | null;
  date: string | null;
}

// Deferred record creation started outside the journal (e.g. from the Lead
// Card). It carries the client the appointment must be created for to the
// appointment journal, where the user picks the specialist/date/time before the
// record modal is actually opened. The modal stays closed until then, so no
// creation request can be sent without a specialist (master) id.
export interface RecordCreationContext {
  clientId: number;
  clientName: string | null;
}

// Клиент записи на момент её загрузки в окно или последнего сохранения
// (issue/11575). Смена клиента в поле «Поиск» его не меняет: по нему окно
// видит несохранённую смену клиента и возвращает клиента при отказе.
export interface SavedRecordClient {
  clientId: number | null;
  clientName: string | null;
}

interface RecordTagsWindowPayload {
  recordId: number;
  date: string;
}

interface RecordModalState {
  isOpen: boolean;
  mode: ModalMode;
  record: RecordInterface | null;
  unsavedRecordForm: RecordDetailsForm | null;
  employee: UpdatedEmployeeInterface | null;
  time: string | null;
  endTime: string | null;
  clientId: number | null;
  clientName: string | null;
  savedClient: SavedRecordClient | null;
  // Сколько раз «Уйти без изменений» вернул клиента сохранённой записи. Вкладка
  // «Детали записи» по нему возвращает поля клиента и номер (task/11629).
  savedClientRestoreCount: number;
  branchId: number | null;
  tab: MenuTabEnum;
  date: string | null;
  isLoading: boolean;
  hasChanges: boolean;
  isInitialized: boolean;
  nextTab: MenuTabEnum | null;
  prepareRecordCreationResponse: TEmployeeData | null;
  recordIdForOpen: number | null;
  clientAllRecords: OtherRecordsInterface[];
  warnings: Warnings[];
  isFullyPaid: boolean;
  creationContext: RecordCreationContext | null;
  // Запись, для которой открыто окно тегов (us/11144). Живёт отдельно от
  // самого окна записи: окно тегов показывается уже после того, как запись
  // создана и модалка записи закрыта.
  recordTagsWindowRecordId: number | null;
  // День этой записи (YYYY-MM-DD): после сохранения тегов по нему обновляется
  // журнал записей (issue/11332).
  recordTagsWindowDate: string | null;
}

const initialState: RecordModalState = {
  isOpen: false,
  mode: 'create',
  record: null,
  unsavedRecordForm: null,
  employee: null,
  time: null,
  endTime: null,
  clientId: null,
  clientName: null,
  savedClient: null,
  savedClientRestoreCount: 0,
  branchId: null,
  tab: MenuTabEnum.RecordDetails,
  date: null,
  isLoading: false,
  hasChanges: false,
  isInitialized: false,
  nextTab: null,
  prepareRecordCreationResponse: null,
  recordIdForOpen: null,
  clientAllRecords: [],
  warnings: [],
  isFullyPaid: false,
  creationContext: null,
  recordTagsWindowRecordId: null,
  recordTagsWindowDate: null,
};

const recordModalSlice = createSlice({
  name: 'recordModal',
  initialState,
  reducers: {
    openRecordModal: (state, action: PayloadAction<RecordModalPayload>) => {
      const {
        record,
        employee,
        mode,
        time,
        endTime,
        clientId,
        clientName,
        branchId,
        date,
      } = action.payload;
      state.isOpen = true;
      state.mode = mode;
      state.record = record ?? null;
      state.employee = employee ?? null;
      state.time = time ?? null;
      state.endTime = endTime ?? null;
      state.clientId = clientId ?? null;
      state.clientName = clientName ?? null;
      state.savedClient = null;
      state.branchId = branchId ?? null;
      state.date = date;
      state.tab = MenuTabEnum.RecordDetails;
      state.isLoading = true;
      state.hasChanges = false;
      state.isInitialized = false;
      state.prepareRecordCreationResponse = null;
      state.nextTab = null;
      state.recordIdForOpen = null;
      state.clientAllRecords = [];
      state.unsavedRecordForm = null;
      state.warnings = [];
      state.isFullyPaid = false;
    },
    closeRecordModal: (state) => {
      state.isOpen = false;
      state.mode = 'create';
      state.record = null;
      state.employee = null;
      state.time = null;
      state.endTime = null;
      state.clientId = null;
      state.clientName = null;
      state.savedClient = null;
      state.branchId = null;
      state.date = null;
      state.tab = MenuTabEnum.RecordDetails;
      state.isLoading = false;
      state.hasChanges = false;
      state.isInitialized = false;
      state.prepareRecordCreationResponse = null;
      state.nextTab = null;
      state.recordIdForOpen = null;
      state.clientAllRecords = [];
      state.unsavedRecordForm = null;
      state.warnings = [];
      state.isFullyPaid = false;
    },
    setClientId: (state, action: PayloadAction<number | null>) => {
      state.clientId = action.payload;
      if (state.record?.ClientId) {
        state.record.ClientId = action.payload || null;
      }
    },
    setClientName: (state, action: PayloadAction<string | null>) => {
      state.clientName = action.payload;
    },
    setSavedClient: (
      state,
      action: PayloadAction<SavedRecordClient | null>
    ) => {
      state.savedClient = action.payload;
    },
    // Отказ от несохранённых правок возвращает окну клиента сохранённой
    // записи: вкладки окна читают clientId и record.ClientId.
    restoreSavedClient: (state) => {
      if (!state.savedClient) return;

      const { clientId, clientName } = state.savedClient;

      state.clientId = clientId;
      state.clientName = clientName;
      state.savedClientRestoreCount += 1;
      if (state.record) {
        state.record.ClientId = clientId;
      }
    },
    setRecord: (state, action: PayloadAction<RecordInterface>) => {
      state.record = action.payload;
    },
    setClientAllRecords: (
      state,
      action: PayloadAction<OtherRecordsInterface[]>
    ) => {
      state.clientAllRecords = action.payload;
    },
    setRecordTab: (state, action: PayloadAction<MenuTabEnum>) => {
      state.tab = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setMode: (state, action: PayloadAction<ModalMode>) => {
      state.mode = action.payload;
    },
    setHasChanges: (state, action: PayloadAction<boolean>) => {
      state.hasChanges = action.payload;
    },
    setIsInitialized: (state, action: PayloadAction<boolean>) => {
      state.isInitialized = action.payload;
    },
    setDate: (state, action: PayloadAction<string | null>) => {
      state.date = action.payload;
    },
    setEmployee: (state, action: PayloadAction<UpdatedEmployeeInterface>) => {
      state.employee = action.payload;
    },
    setNextTab: (state, action: PayloadAction<MenuTabEnum | null>) => {
      state.nextTab = action.payload;
    },
    setPrepareRecordCreationResponse: (
      state,
      action: PayloadAction<TEmployeeData | null>
    ) => {
      state.prepareRecordCreationResponse = action.payload;
    },
    openRecordByIdInModal: (state, action: PayloadAction<number>) => {
      state.isOpen = true;
      state.tab = MenuTabEnum.RecordDetails;
      state.isLoading = true;
      state.hasChanges = false;
      state.isInitialized = false;
      state.recordIdForOpen = action.payload;
      state.savedClient = null;
      state.branchId = null;
      state.prepareRecordCreationResponse = null;
      state.nextTab = null;
      state.mode = 'edit';
      state.unsavedRecordForm = null;
      state.warnings = [];
    },
    setRecordIdForOpen: (state, action: PayloadAction<number | null>) => {
      state.recordIdForOpen = action.payload;
    },
    setUnsavedRecordForm: (
      state,
      action: PayloadAction<RecordDetailsForm | null>
    ) => {
      state.unsavedRecordForm = action.payload;
    },
    setWarnings: (state, action: PayloadAction<Warnings[]>) => {
      state.warnings = action.payload;
    },
    setIsFullyPaid: (state, action: PayloadAction<boolean>) => {
      state.isFullyPaid = action.payload;
    },
    openRecordTagsWindow: (
      state,
      action: PayloadAction<RecordTagsWindowPayload>
    ) => {
      state.recordTagsWindowRecordId = action.payload.recordId;
      state.recordTagsWindowDate = action.payload.date;
    },
    closeRecordTagsWindow: (state) => {
      state.recordTagsWindowRecordId = null;
      state.recordTagsWindowDate = null;
    },
    setRecordCreationContext: (
      state,
      action: PayloadAction<RecordCreationContext>
    ) => {
      state.creationContext = action.payload;
    },
    clearRecordCreationContext: (state) => {
      state.creationContext = null;
    },
  },
});

const { actions, reducer } = recordModalSlice;

export const {
  openRecordModal,
  closeRecordModal,
  setClientId,
  setRecord,
  setClientAllRecords,
  setClientName,
  setSavedClient,
  restoreSavedClient,
  setRecordTab,
  setIsLoading,
  setHasChanges,
  setIsInitialized,
  setDate,
  setEmployee,
  setNextTab,
  setPrepareRecordCreationResponse,
  openRecordByIdInModal,
  setRecordIdForOpen,
  setUnsavedRecordForm,
  setMode,
  setWarnings,
  setIsFullyPaid,
  setRecordCreationContext,
  clearRecordCreationContext,
  openRecordTagsWindow,
  closeRecordTagsWindow,
} = actions;

export const recordModalReducer = reducer;
